import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, CalendarClock, Plus, Trash2, Loader2, Clock, CalendarDays, Repeat, AlertTriangle, CalendarX,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { useAdmin } from "@/contexts/AdminContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShiftRequestDialog } from "@/components/admin/schedule/ShiftRequestDialog";
import { ShiftRequestsPanel } from "@/components/admin/schedule/ShiftRequestsPanel";
import { MissedShiftsCard } from "@/components/admin/schedule/MissedShiftsCard";
import { MyShiftsCard } from "@/components/admin/schedule/MyShiftsCard";
import { OpenShiftsBoard } from "@/components/admin/schedule/OpenShiftsBoard";
import { WeekSchedulePdfButtons } from "@/components/admin/schedule/WeekSchedulePdfButtons";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const shiftHours = (s) => {
  const a = new Date(s.start).getTime(); const b = new Date(s.end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, (b - a) / 3600000);
};

const REPEAT_OPTIONS = [1, 2, 4, 6, 8, 12, 16, 26];

export default function SchedulePage() {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const isDispatcher = (admin?.role || "").toLowerCase() === "dispatcher";

  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(null);            // dispatcher view payload
  const [shifts, setShifts] = useState([]);          // management view list
  const [dispatchers, setDispatchers] = useState([]);
  const [form, setForm] = useState({ dispatcher_id: "", start: "", end: "", note: "", repeat_weeks: "1" });
  const [saving, setSaving] = useState(false);
  const [reqShift, setReqShift] = useState(null);
  const [reqKey, setReqKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isDispatcher) {
        const { data } = await adminApi.get("/admin/schedule/mine");
        setMine(data);
      } else {
        const [sc, dp, mn] = await Promise.all([
          adminApi.get("/admin/schedule"),
          adminApi.get("/admin/timesheets/dispatchers"),
          adminApi.get("/admin/schedule/mine"),
        ]);
        setShifts(sc.data?.shifts || []);
        setDispatchers(dp.data?.dispatchers || []);
        setMine(mn.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }, [isDispatcher]);

  useEffect(() => { load(); }, [load]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return (shifts || []).filter((s) => new Date(s.end).getTime() >= now);
  }, [shifts]);

  const createShift = async () => {
    if (!form.dispatcher_id || !form.start || !form.end) {
      toast.error("Pick a staff member and both start and end times");
      return;
    }
    setSaving(true);
    try {
      // datetime-local has no offset — send the real instant in the user's own timezone.
      const { data } = await adminApi.post("/admin/schedule", {
        ...form,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        repeat_weeks: Number(form.repeat_weeks) || 1,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      toast.success(data?.count > 1 ? `${data.count} weekly shifts assigned` : "Shift assigned");
      setForm({ dispatcher_id: form.dispatcher_id, start: "", end: "", note: "", repeat_weeks: form.repeat_weeks });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign shift");
    } finally {
      setSaving(false);
    }
  };

  const removeShift = async (id) => {
    if (!window.confirm("Remove this shift?")) return;
    try {
      await adminApi.delete(`/admin/schedule/${id}`);
      setShifts((prev) => prev.filter((s) => s.id !== id));
      toast.success("Shift removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove shift");
    }
  };

  const removeSeries = async (s) => {
    if (!window.confirm("Remove this shift and every later week in this repeating series?")) return;
    try {
      const { data } = await adminApi.delete(`/admin/schedule/series/${s.series_id}`, { params: { from_start: s.start } });
      toast.success(`Removed ${data?.deleted || 0} shifts`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove series");
    }
  };


  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-5">
        <Button
          variant="ghost" size="sm" onClick={() => navigate("/admin")}
          data-testid="schedule-back-to-console"
          className="h-8 px-2 -ml-2 text-xs text-zinc-500 hover:text-amber-500"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5"/>Back to Console
        </Button>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2" data-testid="schedule-title">
              <CalendarClock className="h-7 w-7 text-amber-500"/>Schedule
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {isDispatcher
                ? "Your upcoming shifts and hours worked this pay period."
                : "Assign shifts to staff — they appear in each person's own schedule."}
            </p>
          </div>
          <WeekSchedulePdfButtons/>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-400">
            <Loader2 className="h-6 w-6 animate-spin"/>
          </div>
        ) : isDispatcher ? (
          /* ───────── Dispatcher self view (read-only) ───────── */
          <>
            <Card className="p-4 flex items-center justify-between" data-testid="schedule-period-card">
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Clock className="h-4 w-4"/>Hours this pay period (Mon–Sun)
              </div>
              <div className="text-2xl font-black tabular-nums text-emerald-600 dark:text-emerald-300" data-testid="schedule-period-hours">
                {(mine?.period_hours ?? 0).toFixed(2)} hrs
              </div>
            </Card>

            <MyShiftsCard shifts={mine?.shifts || []} onRequest={setReqShift}/>
            <OpenShiftsBoard refreshKey={reqKey} onChanged={load}/>
            <ShiftRequestsPanel refreshKey={reqKey} onChanged={load}/>
          </>
        ) : (
          /* ───────── Management view (assign + list) ───────── */
          <>
            <Card className="p-4" data-testid="schedule-assign-card">
              <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
                <Plus className="h-4 w-4"/>Assign a shift
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Staff member</Label>
                  <Select value={form.dispatcher_id} onValueChange={(v) => setForm((f) => ({ ...f, dispatcher_id: v }))}>
                    <SelectTrigger data-testid="schedule-dispatcher-select" className="mt-1"><SelectValue placeholder="Select a staff member…"/></SelectTrigger>
                    <SelectContent>
                      {dispatchers.map((d) => (
                        <SelectItem key={d.id} value={d.id} data-testid={`schedule-dispatcher-opt-${d.id}`}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input type="datetime-local" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} data-testid="schedule-start-input" className="mt-1"/>
                </div>
                <div>
                  <Label className="text-xs">End</Label>
                  <Input type="datetime-local" value={form.end} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} data-testid="schedule-end-input" className="mt-1"/>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Repeat weekly</Label>
                  <Select value={form.repeat_weeks} onValueChange={(v) => setForm((f) => ({ ...f, repeat_weeks: v }))}>
                    <SelectTrigger data-testid="schedule-repeat-select" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {REPEAT_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)} data-testid={`schedule-repeat-opt-${n}`}>
                          {n === 1 ? "Does not repeat" : `Every week for ${n} weeks`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Note (optional)</Label>
                  <Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="schedule-note-input" className="mt-1" rows={2} placeholder="e.g. Night dispatch desk"/>
                </div>
              </div>
              <Button onClick={createShift} disabled={saving} data-testid="schedule-assign-btn" className="mt-3 h-10 bg-amber-500 hover:bg-amber-600 text-white font-bold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Plus className="h-4 w-4 mr-2"/>}Assign Shift
              </Button>
            </Card>

            <Card className="p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4"/>Upcoming shifts ({upcoming.length})
              </div>
              {upcoming.length === 0 ? (
                <div className="text-sm text-zinc-400 py-6 text-center" data-testid="schedule-mgmt-empty">No upcoming shifts assigned.</div>
              ) : (
                <ul className="space-y-2" data-testid="schedule-mgmt-list">
                  {upcoming.map((s) => (
                    <li key={s.id} className="flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2.5" data-testid={`schedule-mgmt-shift-${s.id}`}>
                      <div>
                        <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                          {s.open ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`schedule-open-badge-${s.id}`}>
                              Open shift{s.claim ? ` · claimed by ${s.claim.staff_name}` : ""}
                            </span>
                          ) : s.dispatcher_name}
                          {s.series_id && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`schedule-series-badge-${s.id}`}>
                              <Repeat className="h-3 w-3"/>Weekly
                            </span>
                          )}
                          {s.missed && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`schedule-missed-badge-${s.id}`}>
                              <AlertTriangle className="h-3 w-3"/>Missed · no clock-in
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-zinc-500">{fmtDateTime(s.start)} → {fmtDateTime(s.end)} · {shiftHours(s).toFixed(1)}h</div>
                        {s.note ? <div className="text-[11px] text-zinc-400 mt-0.5">{s.note}</div> : null}
                      </div>
                      <div className="flex items-center shrink-0">
                        {s.series_id && (
                          <Button variant="ghost" size="icon" title="Remove this and later weeks" onClick={() => removeSeries(s)} data-testid={`schedule-delete-series-${s.id}`} className="text-zinc-400 hover:text-rose-500 h-8 w-8">
                            <CalendarX className="h-4 w-4"/>
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title="Remove this shift" onClick={() => removeShift(s.id)} data-testid={`schedule-delete-${s.id}`} className="text-zinc-400 hover:text-rose-500 h-8 w-8">
                          <Trash2 className="h-4 w-4"/>
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <MyShiftsCard shifts={mine?.shifts || []} onRequest={setReqShift} title="My upcoming shifts" hideWhenEmpty/>
            <OpenShiftsBoard refreshKey={reqKey} onChanged={load}/>
            <ShiftRequestsPanel refreshKey={reqKey} onChanged={load}/>
            <MissedShiftsCard refreshKey={reqKey}/>
          </>
        )}
        <ShiftRequestDialog shift={reqShift} open={!!reqShift} onOpenChange={(o) => { if (!o) setReqShift(null); }} onDone={() => setReqKey((k) => k + 1)}/>
      </div>
    </div>
  );
}
